/**
 * Programme validation and completion tracking utilities.
 * Provides validation rules and progress calculation for programme design.
 * @module utils/validation
 */

import type {
  DeliveryPattern,
  ElectiveDefinition,
  ElectiveGroup,
  EmbeddedAward,
  Module,
  ModuleAssessment,
  PLO,
  Programme,
  ProgrammeVersion,
  Stage,
} from "@/types";
import type { CasAwardsData } from "@/types/cas";

import { resolveEffectiveAssessments } from "./assessment-overrides";
import { assessmentWeightLedger, isBalanced, worstEntry } from "./assessment-weight";
import { getAwardCreditProjection, getAwardCreditRangeLabel } from "./award-pathways";
import { isCasProgramme, isFeAwardType, isHeAwardType, outcomeBand } from "./award-types";
import { casGroupStatus, getRuleGroupsForAward, getRuleItemsForAward } from "./cas-data";
import { getElectiveOptionSelection } from "./electives";
import {
  AWARD_TYPE_NFQ_MAP,
  defaultPatternFor,
  getEffectiveCredits,
  getEffectiveProgrammeCredits,
} from "./helpers";
import { canonicalTeachingPeriod } from "./legacy-vocabulary";
import { getStepCompletion } from "./step-completion";
import {
  placementName,
  programmeTeachingPeriodLabel,
  stageTeachingPeriodOptions,
} from "./teaching-period";

export interface ValidationFlag {
  type: "error" | "warn";
  msg: string;
  step: string;
  moduleId?: string;
  versionId?: string;
  /** The programme outcome the finding is about. */
  ploId?: string;
  /** Follows from the finding with the same `ploId`, which lists it beneath itself. */
  nested?: boolean;
}

/** An outcome with no wording yet, which nothing downstream can use. */
function isBlankOutcome(plo: PLO): boolean {
  return !(plo.text ?? "").trim();
}

// Recognised delivery modalities carry a valid default split that sums to 100%
// (e.g. blended is 30/40/30), so a version using one needs no manual pattern.
const KNOWN_DELIVERY_MODALITIES = new Set(["F2F", "BLENDED", "ONLINE"]);

const EMBEDDED_AWARD_KINDS = new Set(["embedded", "exit", "embedded-and-exit", "cas-component"]);

interface AssessmentValidationContext {
  assessments: ModuleAssessment[];
  teachingWeeks: number;
  version?: ProgrammeVersion;
  sharedChanged: boolean;
}

function placedInStage(stage: Stage, moduleId: string): boolean {
  return (
    (stage.moduleIds ?? []).includes(moduleId) ||
    (stage.modules ?? []).some((placement) => placement.moduleId === moduleId)
  );
}

function placedIn(version: ProgrammeVersion, moduleId: string): boolean {
  return (version.stages ?? []).some((stage) => placedInStage(stage, moduleId));
}

function assessmentContextsForModule(
  module: Module,
  versions: ProgrammeVersion[],
): AssessmentValidationContext[] {
  const owningVersions = versions.filter((version) => placedIn(version, module.id));

  if (owningVersions.length === 0) {
    const minimumTeachingWeeks = versions
      .flatMap((version) => version.stages ?? [])
      .reduce((minimum, stage) => Math.min(minimum, stage.teachingWeeks ?? 12), 12);
    return [
      {
        assessments: module.assessments ?? [],
        teachingWeeks: minimumTeachingWeeks,
        sharedChanged: false,
      },
    ];
  }

  return owningVersions.map((version) => {
    const owningStage = (version.stages ?? []).find((stage) => placedInStage(stage, module.id));
    const effective = resolveEffectiveAssessments(module, version);
    return {
      assessments: effective.assessments,
      teachingWeeks: owningStage?.teachingWeeks ?? 12,
      version,
      sharedChanged: effective.status === "shared-changed",
    };
  });
}

function assessmentContextPrefix(module: Module, version?: ProgrammeVersion): string {
  const moduleLabel =
    module.title && module.code ? `${module.title} (${module.code})` : module.title || module.code;
  const named = moduleLabel || "Module";
  if (!version) {
    return `${named}:`;
  }
  return `${named}, ${version.label || version.code || "Programme"} version:`;
}

/**
 * Validates first-class embedded, exit, and CAS component award records.
 */
export function validateEmbeddedAwards(p: Programme): ValidationFlag[] {
  const flags: ValidationFlag[] = [];
  const awards = p.embeddedAwards ?? [];
  const modulesById = new Map((p.modules ?? []).map((module) => [module.id, module]));
  const versions = p.versions ?? [];
  const versionIds = new Set(versions.map((version) => version.id));
  const stageIds = new Set(
    versions.flatMap((version) => (version.stages ?? []).map((stage) => stage.id)),
  );
  const seenIds = new Set<string>();

  awards.forEach((award: EmbeddedAward, index) => {
    const title = (award.title ?? "").trim();
    const label = title ? `Award "${title}"` : `Embedded award ${index + 1}`;
    const step = "award-pathway";

    if (!award.id || seenIds.has(award.id)) {
      flags.push({
        type: "error",
        msg: `${label} must have a unique identifier.`,
        step,
      });
    } else {
      seenIds.add(award.id);
    }
    if (!title) {
      flags.push({
        type: "error",
        msg: `${label} requires a title.`,
        step,
      });
    }
    if (!EMBEDDED_AWARD_KINDS.has(award.kind)) {
      flags.push({
        type: "error",
        msg: `${label} must be identified as embedded, exit, both, or a CAS component award.`,
        step,
      });
    }
    if (!(award.awardType ?? "").trim()) {
      flags.push({
        type: "error",
        msg: `${label} does not yet have an award type.`,
        step,
      });
    }
    if (award.nfqLevel == null) {
      flags.push({
        type: "error",
        msg: `${label} does not yet have an NFQ level.`,
        step,
      });
    } else if (p.nfqLevel != null && Number(award.nfqLevel) > Number(p.nfqLevel)) {
      flags.push({
        type: "warn",
        msg: `${label} is NFQ Level ${award.nfqLevel}, above the parent programme's Level ${p.nfqLevel}.`,
        step,
      });
    }
    if (Number(award.credits ?? 0) <= 0) {
      flags.push({
        type: "error",
        msg: `${label} does not yet have a credit value.`,
        step,
      });
    }

    const linkedStageIds = award.stageIds ?? [];
    const linkedModuleIds = award.moduleIds ?? [];
    if (linkedStageIds.length === 0 && linkedModuleIds.length === 0) {
      flags.push({
        type: "error",
        msg: `${label} must link to at least one stage or module.`,
        step,
      });
    }

    const missingStageIds = linkedStageIds.filter((stageId) => !stageIds.has(stageId));
    if (missingStageIds.length > 0) {
      flags.push({
        type: "error",
        msg: `${label} links to ${missingStageIds.length} stage${missingStageIds.length === 1 ? "" : "s"} that no longer exist${missingStageIds.length === 1 ? "s" : ""}.`,
        step,
      });
    }
    const missingModuleIds = linkedModuleIds.filter((moduleId) => !modulesById.has(moduleId));
    if (missingModuleIds.length > 0) {
      flags.push({
        type: "error",
        msg: `${label} links to ${missingModuleIds.length} module${missingModuleIds.length === 1 ? "" : "s"} that no longer exist${missingModuleIds.length === 1 ? "s" : ""}.`,
        step,
      });
    }
    const missingVersionIds = (award.versionIds ?? []).filter(
      (versionId) => !versionIds.has(versionId),
    );
    if (missingVersionIds.length > 0) {
      flags.push({
        type: "error",
        msg: `${label} links to ${missingVersionIds.length} programme version${missingVersionIds.length === 1 ? "" : "s"} that no longer exist${missingVersionIds.length === 1 ? "s" : ""}.`,
        step,
      });
    }

    if (
      (linkedStageIds.length > 0 || linkedModuleIds.length > 0) &&
      missingStageIds.length === 0 &&
      missingModuleIds.length === 0 &&
      Number(award.credits ?? 0) > 0
    ) {
      const projection = getAwardCreditProjection(p, award);
      const linkedCredits = getAwardCreditRangeLabel(projection);
      if (projection.linkedModules.length === 0) {
        flags.push({
          type: "error",
          msg: `${label}'s links do not currently resolve to any contributing modules.`,
          step,
        });
      } else if (projection.status === "shortfall") {
        flags.push({
          type: "error",
          msg: `${label} defines ${projection.declaredCredits} credits, but its linked learner pathways provide at most ${projection.maximumCredits}.`,
          step,
        });
      } else if (projection.status === "excess") {
        flags.push({
          type: "error",
          msg: `${label} defines ${projection.declaredCredits} credits, but its linked learner pathways require at least ${projection.minimumCredits}.`,
          step,
        });
      } else if (projection.incompleteChoiceSlots.length > 0) {
        flags.push({
          type: "warn",
          msg: `${label} cannot be fully reconciled until these choice slots have complete representative selections: ${projection.incompleteChoiceSlots.join(", ")}. Current linked range: ${linkedCredits}.`,
          step,
        });
      }
    }

    if (award.kind === "cas-component" && !(award.casComponentCode ?? "").trim()) {
      flags.push({
        type: "error",
        msg: `${label} requires its QQI CAS component code.`,
        step: "structure",
      });
    }
  });

  return flags;
}

/**
 * Validates CAS rule compliance for FE programmes when CAS data is available.
 * Returns rule-specific flags for display in the CAS step and QQI flags panel.
 */
export function validateCasSelection(
  p: Programme,
  casData: CasAwardsData | null,
): ValidationFlag[] {
  const flags: ValidationFlag[] = [];
  if (!isFeAwardType(p.awardType ?? "")) {
    return flags;
  }

  const majorAwardCode = (p.casMajorAwardCode ?? "").trim();
  if (!majorAwardCode) {
    // Identity offers a CAS major award only to an FE Major Award, so asking
    // any other award type to link one points at a control it does not have.
    if (isCasProgramme(p)) {
      flags.push({
        type: "error",
        msg: "Choose the CAS major award this FE Major Award leads to.",
        step: "cas-picker",
      });
    }
    return flags;
  }

  if (!casData) {
    flags.push({
      type: "warn",
      msg: "CAS rules could not be validated because CAS data is unavailable.",
      step: "cas-picker",
    });
    return flags;
  }

  const groups = getRuleGroupsForAward(casData, majorAwardCode);
  const items = getRuleItemsForAward(casData, majorAwardCode);
  if (groups.length === 0 || items.length === 0) {
    flags.push({
      type: "warn",
      msg: `No CAS rule data was found for ${majorAwardCode}.`,
      step: "cas-picker",
    });
    return flags;
  }

  const componentCredits = new Map(
    (casData.components ?? []).map((c) => [c.code, Number(c.credit_value) || 0]),
  );
  const casModules = (p.modules ?? []).filter((m: Module) => m.casSource === true);
  const addedCodes = new Set(
    casModules
      .map((m: Module) => String(m.casComponentCode ?? m.code ?? "").trim())
      .filter(Boolean),
  );

  const mandatoryCodes = Array.from(
    new Set(
      items
        .filter((i) => i.is_mandatory && typeof i.component_code === "string")
        .map((i) => i.component_code as string),
    ),
  );
  const missingMandatory = mandatoryCodes.filter((code) => !addedCodes.has(code));
  if (missingMandatory.length > 0) {
    const sample = missingMandatory.slice(0, 4).join(", ");
    flags.push({
      type: "error",
      msg: `Missing ${missingMandatory.length} mandatory CAS component${missingMandatory.length === 1 ? "" : "s"}: ${sample}${missingMandatory.length > 4 ? "..." : ""}.`,
      step: "cas-picker",
    });
  }

  groups.forEach((group) => {
    const { codes, selectedCredits, minCredits } = casGroupStatus(
      group,
      items,
      addedCodes,
      (code) => componentCredits.get(code) ?? 0,
    );
    if (codes.length > 0 && minCredits > 0 && selectedCredits < minCredits) {
      flags.push({
        type: group.group_type === "mandatory" ? "error" : "warn",
        msg: `${group.group_label}: selected ${selectedCredits}/${minCredits} required credits.`,
        step: "cas-picker",
      });
    }
  });

  const ruleCodes = new Set(
    items
      .map((i) => i.component_code)
      .filter((code): code is string => typeof code === "string" && code.length > 0),
  );
  const outsideRuleSet = Array.from(addedCodes).filter((code) => !ruleCodes.has(code));
  if (outsideRuleSet.length > 0) {
    flags.push({
      type: "warn",
      msg: `${outsideRuleSet.length} selected CAS component${outsideRuleSet.length === 1 ? " is" : "s are"} outside the award rule set.`,
      step: "cas-picker",
    });
  }

  return flags;
}

/** The teaching period option values a stage offers, by its count. */
function teachingPeriodValues(stage: Stage): Set<string> {
  const count = Number(stage.semesterCount ?? 0);
  return new Set(stageTeachingPeriodOptions(count).map((option) => option.value));
}

/**
 * Validates a programme and returns an array of validation flags.
 * Checks identity fields, credit totals, versions, stages, PLOs, and mappings.
 */
export function validateProgramme(p: Programme): ValidationFlag[] {
  const flags: ValidationFlag[] = [];
  const sumCredits = (p.modules ?? []).reduce(
    (acc: number, m: Module) => acc + (Number(m.credits) ?? 0),
    0,
  );

  if (!p.title.trim()) {
    flags.push({
      type: "error",
      msg: "Programme title is missing.",
      step: "identity",
    });
  }
  if (!p.nfqLevel) {
    flags.push({
      type: "error",
      msg: "NFQ level is missing.",
      step: "identity",
    });
  }
  const nfqLevel = Number(p.nfqLevel);
  const isFe = isFeAwardType(p.awardType ?? "");
  const minNfq = isFe ? 1 : 6;
  const maxNfq = isFe ? 6 : 10;

  if (p.nfqLevel && (nfqLevel < minNfq || nfqLevel > maxNfq)) {
    flags.push({
      type: "error",
      msg: `NFQ level must be between ${minNfq} and ${maxNfq}.`,
      step: "identity",
    });
  }
  const expectedNfqLevel = AWARD_TYPE_NFQ_MAP[(p.awardType ?? "").trim()];
  if (p.nfqLevel && expectedNfqLevel && nfqLevel !== expectedNfqLevel) {
    flags.push({
      type: "warn",
      msg: `${p.awardType} is normally an NFQ Level ${expectedNfqLevel} award, but this programme is set to Level ${nfqLevel}.`,
      step: "identity",
    });
  }
  if (!p.awardType.trim()) {
    flags.push({
      type: "warn",
      msg: "Award type is missing.",
      step: "identity",
    });
  } else if (!p.awardTypeIsOther && !isFeAwardType(p.awardType) && !isHeAwardType(p.awardType)) {
    flags.push({
      type: "warn",
      msg: `Award type is recorded as '${p.awardType}', which is not in the current list. Choose a replacement.`,
      step: "identity",
    });
  }
  flags.push(...validateEmbeddedAwards(p));

  const awardStandardIds = p.awardStandardIds ?? [];
  const seenStandardIds = new Set<string>();
  awardStandardIds.some((id) => {
    if (id) {
      if (seenStandardIds.has(id)) {
        flags.push({
          type: "error",
          msg: "The same award standard is selected more than once.",
          step: "identity",
        });
        return true;
      }
      seenStandardIds.add(id);
    }
    return false;
  });

  if ((p.totalCredits ?? 0) <= 0) {
    flags.push({
      type: "error",
      msg: "Total programme credits are missing/zero.",
      step: "structure",
    });
  }

  // Calculate mandatory vs elective credits
  const mandatoryModules = (p.modules ?? []).filter((m: Module) => !m.isElective);
  const mandatoryCredits = mandatoryModules.reduce(
    (acc: number, m: Module) => acc + (Number(m.credits) ?? 0),
    0,
  );

  const electiveDefinitions = p.electiveDefinitions ?? [];

  // If no electives configured, use traditional credit check
  if (electiveDefinitions.length === 0) {
    if ((p.totalCredits ?? 0) > 0 && sumCredits !== p.totalCredits) {
      flags.push({
        type: "error",
        msg: `Credits mismatch: the modules add up to ${sumCredits} credits, and the programme total is ${p.totalCredits}.`,
        step: "structure",
      });
    }
  } else {
    // Electives are configured - validate elective structure.
    // Students complete every choice slot by choosing one option per slot.

    // Check each persisted definition (choice slot) and group (option).
    const moduleGroupAssignments = new Map<string, { groupId: string; groupName: string }[]>();

    electiveDefinitions.forEach((def: ElectiveDefinition, defIdx: number) => {
      const defDisplayName = def.name ?? `Choice slot ${defIdx + 1}`;
      const defCode = def.code ?? "";
      const defLabel = defCode ? `[${defCode}] ${defDisplayName}` : defDisplayName;
      const defCredits = def.credits ?? 0;
      const groups = def.groups ?? [];

      // Check: choice slot has no options.
      if (groups.length === 0) {
        flags.push({
          type: "error",
          msg: `${defLabel}: no options defined. Add at least one option for learners to complete.`,
          step: "electives",
        });
      }

      // Check: choice slot has no credits set.
      if (defCredits === 0 && groups.length > 0) {
        flags.push({
          type: "error",
          msg: `${defLabel}: options exist but the choice slot has no credit value.`,
          step: "electives",
        });
      }

      // Check each option within this choice slot.
      groups.forEach((g: ElectiveGroup, grpIdx: number) => {
        const groupDisplayName = g.name ?? `Option ${grpIdx + 1}`;
        const groupCode = g.code ?? "";
        const groupLabel = groupCode ? `[${groupCode}] ${groupDisplayName}` : groupDisplayName;
        const fullGroupName = `${defLabel} → ${groupLabel}`;

        // Check: option has no modules assigned.
        if (!g.moduleIds || g.moduleIds.length === 0) {
          flags.push({
            type: "error",
            msg: `${fullGroupName}: no modules assigned. Add at least one eligible module to this option so learners can make a valid choice.`,
            step: "electives",
          });
        } else {
          // Track module-to-option assignments.
          g.moduleIds.forEach((mId: string) => {
            if (!moduleGroupAssignments.has(mId)) {
              moduleGroupAssignments.set(mId, []);
            }
            moduleGroupAssignments.get(mId)?.push({ groupId: g.id, groupName: fullGroupName });
          });

          // Check: option includes a non-elective module.
          const groupModules = (p.modules ?? []).filter((m: Module) => g.moduleIds.includes(m.id));
          const nonElectiveInGroup = groupModules.filter((m: Module) => !m.isElective);
          if (nonElectiveInGroup.length > 0) {
            flags.push({
              type: "warn",
              msg: `${fullGroupName}: contains ${nonElectiveInGroup.length} mandatory module(s).`,
              step: "electives",
            });
          }

          const optionSelection = getElectiveOptionSelection(g, p.modules ?? []);
          if (!optionSelection.selectionCountValid) {
            flags.push({
              type: "error",
              msg: `${fullGroupName}: requires ${optionSelection.configuredSelectionCount} modules, but only ${optionSelection.optionModules.length} module${optionSelection.optionModules.length === 1 ? "" : "s"} ${optionSelection.optionModules.length === 1 ? "is" : "are"} assigned. Add more modules or reduce the required selection count. Error: fix before progressing workflow gates.`,
              step: "electives",
            });
          } else if (optionSelection.requiresModuleChoice && !optionSelection.selectionComplete) {
            flags.push({
              type: "warn",
              msg: `${fullGroupName}: select ${optionSelection.requiredModuleCount} representative module${optionSelection.requiredModuleCount === 1 ? "" : "s"} to preview this learner pathway. Choose representative modules so pathway totals and downstream checks are accurate. Advisory warning (does not block editing).`,
              step: "electives",
            });
          } else if (optionSelection.credits !== defCredits) {
            flags.push({
              type: "error",
              msg: `${fullGroupName}: pathway credits (${optionSelection.credits}) don't match the choice slot requirement (${defCredits}).`,
              step: "electives",
            });
          }
        }
      });
    });

    // Check: a module appears in multiple options.
    moduleGroupAssignments.forEach((assignments, moduleId) => {
      if (assignments.length > 1) {
        const mod = (p.modules ?? []).find((m: Module) => m.id === moduleId);
        const modName = mod ? (mod.code ?? mod.title ?? moduleId) : moduleId;
        const groupNames = assignments.map((a) => a.groupName).join(", ");
        flags.push({
          type: "warn",
          msg: `Module "${modName}" is assigned to ${assignments.length} options: ${groupNames}.`,
          step: "electives",
        });
      }
    });

    // Credit check: mandatory + learner-taken elective credits should = totalCredits.
    // Students complete every choice slot by choosing one option, so the
    // elective contribution is the learner-taken pathway total rather than the sum
    // of all available elective options.
    const learnerElectiveCredits = getEffectiveProgrammeCredits(p).electiveCredits;
    const expectedTotal = mandatoryCredits + learnerElectiveCredits;
    if (expectedTotal !== (p.totalCredits ?? 0) && learnerElectiveCredits > 0) {
      flags.push({
        type: "error",
        msg: `Credit check: mandatory (${mandatoryCredits}) + electives (${learnerElectiveCredits}) = ${expectedTotal}, but programme total is ${p.totalCredits ?? 0}.`,
        step: "structure",
      });
    }
  }

  // Versions
  if (!Array.isArray(p.versions) || p.versions.length === 0) {
    flags.push({
      type: "error",
      msg: "At least one Programme Version is required (e.g., FT/PT/Online).",
      step: "versions",
    });
  } else {
    const labels = new Set<string>();
    p.versions.forEach((v: ProgrammeVersion, idx: number) => {
      const prefix = `Version ${idx + 1}`;
      if (!v.label || !v.label.trim()) {
        flags.push({
          type: "error",
          msg: `${prefix}: label is missing.`,
          step: "versions",
        });
      } else {
        const norm = v.label.trim().toLowerCase();
        if (labels.has(norm)) {
          flags.push({
            type: "warn",
            msg: `${prefix}: duplicate label ("${v.label}").`,
            step: "versions",
          });
        }
        labels.add(norm);
      }

      // Delivery pattern for selected modality must sum to 100
      if (v.deliveryModality) {
        const mod = v.deliveryModality;
        let pat = (v.deliveryPatterns ?? ({} as Record<string, DeliveryPattern>))[mod];
        // A version with no explicit pattern falls back to the modality default.
        // Recognised modalities (face-to-face, blended, online) carry a valid
        // default split that sums to 100, so they need no manual configuration;
        // unrecognised modalities still require an explicit split to be set.
        if (!pat && KNOWN_DELIVERY_MODALITIES.has(mod)) {
          pat = defaultPatternFor(mod);
        }
        if (!pat) {
          flags.push({
            type: "error",
            msg: `${prefix}: missing delivery pattern for ${mod}.`,
            step: "versions",
          });
        } else {
          const total =
            Number(pat.syncOnlinePct ?? 0) +
            Number(pat.asyncDirectedPct ?? 0) +
            Number(pat.onCampusPct ?? 0);
          if (total !== 100) {
            flags.push({
              type: "error",
              msg: `${prefix}: ${mod} delivery pattern must total 100% (currently ${total}%).`,
              step: "versions",
            });
          }
        }
      }

      if (
        (v.onlineProctoredExams ?? "TBC") === "YES" &&
        !(v.onlineProctoredExamsNotes ?? "").trim()
      ) {
        flags.push({
          type: "warn",
          msg: `${prefix}: online proctored exams marked YES but notes are empty.`,
          step: "versions",
        });
      }

      if ((v.targetCohortSize ?? 0) <= 0) {
        flags.push({
          type: "warn",
          msg: `${prefix}: cohort size is missing/zero. Enter a planned intake size so capacity and workload assumptions remain realistic. Advisory warning (does not block editing).`,
          step: "versions",
        });
      }

      // Stage structure
      if (!Array.isArray(v.stages) || v.stages.length === 0) {
        flags.push({
          type: "error",
          msg: `${prefix}: no stages defined yet. Add at least one stage to describe progression and module placement.`,
          step: "stages",
        });
      } else {
        const stageTargetSum = (v.stages ?? []).reduce(
          (acc: number, s: Stage) => acc + Number(s.creditsTarget ?? 0),
          0,
        );

        const periodLabel = programmeTeachingPeriodLabel(p);
        const firstPlacementByModuleId = new Map<string, string>();
        (v.stages ?? []).forEach((stage) => {
          const stageName = stage.name ?? `Stage ${stage.sequence ?? ""}`;
          (stage.modules ?? []).forEach((assignment) => {
            const module = (p.modules ?? []).find(
              (candidate) => candidate.id === assignment.moduleId,
            );
            const moduleLabel = module?.code || module?.title || assignment.moduleId;
            const priorPlacement = firstPlacementByModuleId.get(assignment.moduleId);
            if (!priorPlacement) {
              firstPlacementByModuleId.set(
                assignment.moduleId,
                placementName(stageName, assignment.semester, periodLabel),
              );
              return;
            }

            flags.push({
              type: "error",
              msg: `${prefix}: Module "${moduleLabel}" is already included in ${priorPlacement}. Remove duplicate placement from ${stageName}.`,
              step: "stages",
            });
          });
        });

        // As the stage summary: a target left at zero is unset, so the sum is
        // no total until every stage has one.
        if (
          (p.totalCredits ?? 0) > 0 &&
          (v.stages ?? []).every((s: Stage) => Number(s.creditsTarget ?? 0) > 0) &&
          stageTargetSum !== Number(p.totalCredits ?? 0)
        ) {
          flags.push({
            type: "error",
            msg: `${prefix}: sum of stage credit targets (${stageTargetSum}) does not match programme total credits (${p.totalCredits}). Update stage targets or programme credits so both totals align.`,
            step: "stages",
          });
        }

        (v.stages ?? []).forEach((s: Stage) => {
          const stageMods = (s.modules ?? []).map((x) => x.moduleId);
          const placedDefinitionIds = new Set<string>();

          (s.electiveChoiceSlots ?? []).forEach((placement) => {
            const definition = (p.electiveDefinitions ?? []).find(
              (item) => item.id === placement.electiveDefinitionId,
            );
            if (!definition) {
              flags.push({
                type: "warn",
                msg: `${prefix}: ${s.name ?? "stage"} contains an elective choice slot that no longer exists. Remove this stale slot or recreate the elective definition. Advisory warning (does not block editing).`,
                step: "stages",
              });
              return;
            }
            if (placedDefinitionIds.has(definition.id)) {
              flags.push({
                type: "warn",
                msg: `${prefix}: ${s.name ?? "stage"} contains the ${definition.name || definition.code || "elective"} choice slot more than once. Keep one placement per stage to avoid duplicate pathway counting. Advisory warning (does not block editing).`,
                step: "stages",
              });
              return;
            }
            placedDefinitionIds.add(definition.id);

            const optionModuleIds = Array.from(
              new Set((definition.groups ?? []).flatMap((group) => group.moduleIds ?? [])),
            );
            const missingOptionCount = optionModuleIds.filter(
              (moduleId) => !stageMods.includes(moduleId),
            ).length;
            if (missingOptionCount > 0) {
              flags.push({
                type: "warn",
                msg: `${prefix}: ${s.name ?? "stage"} is missing ${missingOptionCount} module option${missingOptionCount === 1 ? "" : "s"} attached to the ${definition.name || definition.code || "elective"} choice slot. Add the missing modules to the stage or adjust the slot definition. Advisory warning (does not block editing).`,
                step: "stages",
              });
            }
          });

          const modulesWithoutTeachingPeriod = (s.modules ?? []).filter(
            (assignment) => !(assignment.semester ?? "").toString().trim(),
          );
          if (Number(s.semesterCount ?? 0) > 0) {
            modulesWithoutTeachingPeriod.forEach((assignment) => {
              const module = (p.modules ?? []).find((m) => m.id === assignment.moduleId);
              const moduleLabel = module?.code || module?.title || assignment.moduleId;
              flags.push({
                type: "error",
                msg: `${prefix}: Module "${moduleLabel}" in ${s.name ?? "stage"} has no teaching period. Choose one of the stage's teaching periods.`,
                step: "stages",
                moduleId: assignment.moduleId,
              });
            });
          } else if (modulesWithoutTeachingPeriod.length > 0) {
            flags.push({
              type: "warn",
              msg: `${prefix}: ${s.name ?? "stage"} has ${modulesWithoutTeachingPeriod.length} module${modulesWithoutTeachingPeriod.length === 1 ? "" : "s"} without a teaching period. Assign a teaching period so timetable and assessment checks can run correctly. Advisory warning (does not block editing).`,
              step: "stages",
            });
          }

          // With no count the period is free text, so there is no list to be off.
          const listed = teachingPeriodValues(s);
          const offListPeriods = [...(s.modules ?? []), ...(s.electiveChoiceSlots ?? [])].filter(
            (placement) => {
              const value =
                canonicalTeachingPeriod(placement.semester?.trim(), Number(s.semesterCount ?? 0)) ??
                "";
              return listed.size > 0 && value !== "" && !listed.has(value);
            },
          );
          if (offListPeriods.length > 0) {
            flags.push({
              type: "warn",
              msg: `${prefix}: ${s.name ?? "stage"} has ${offListPeriods.length} placement${offListPeriods.length === 1 ? "" : "s"} with a teaching period that is not in the current list. Choose a teaching period for each.`,
              step: "stages",
            });
          }

          const creditSum = getEffectiveCredits(p, stageMods).total;

          if ((s.creditsTarget ?? 0) > 0 && creditSum !== Number(s.creditsTarget ?? 0)) {
            flags.push({
              type: "error",
              msg: `${prefix}: ${s.name ?? "stage"} module credits sum to ${creditSum} but target is ${s.creditsTarget}. Adjust module placements or the stage target so stage credits balance.`,
              step: "stages",
            });
          }
        });
      }
    });
  }

  // Outcomes & mapping
  const band = outcomeBand(p);
  const range = `aim for ${band.min}–${band.max} on this award`;
  if ((p.plos ?? []).length < band.min) {
    flags.push({
      type: "warn",
      msg: `Programme outcomes: fewer than ${band.min} (${range}). Add outcomes to cover the programme's breadth. Advisory warning (does not block editing).`,
      step: "outcomes",
    });
  }
  if ((p.plos ?? []).length > band.max) {
    flags.push({
      type: "warn",
      msg: `Programme outcomes: more than ${band.max} (${range}). Consolidate overlapping outcomes to keep assessment and mapping manageable. Advisory warning (does not block editing).`,
      step: "outcomes",
    });
  }

  (p.plos ?? []).forEach((plo, index) => {
    if (isBlankOutcome(plo)) {
      flags.push({
        type: "error",
        msg: `Programme outcome ${index + 1} has no text. Write the outcome or remove it.`,
        step: "outcomes",
        ploId: plo.id,
      });
    }
  });

  // One flag per module, so each names the module it sends the reader to.
  (p.modules ?? [])
    .filter((m: Module) => !m.mimlos || m.mimlos.length === 0)
    .forEach((m: Module) => {
      flags.push({
        type: "error",
        msg: `${assessmentContextPrefix(m)} No module outcomes yet. Add at least one outcome so its assessments and mapping can be completed.`,
        step: "mimlos",
        moduleId: m.id,
      });
    });

  // Assessment validation runs against each owning programme version's effective
  // structure. Modules not yet placed in a version retain the shared validation path.
  const versions = p.versions ?? [];
  const ledger = assessmentWeightLedger(p);

  (p.modules ?? []).forEach((m: Module) => {
    const ledgerEntries = ledger.entriesFor(m.id);
    assessmentContextsForModule(m, versions).forEach(
      ({ assessments, teachingWeeks, version, sharedChanged }) => {
        const prefix = assessmentContextPrefix(m, version);
        const moduleDescriptor =
          m.title && m.code ? `${m.title} (${m.code})` : (m.title ?? m.code ?? "the module");
        const flagContext = {
          step: "assessments",
          moduleId: m.id,
          ...(version ? { versionId: version.id } : {}),
        };

        if (sharedChanged) {
          flags.push({
            type: "warn",
            msg: `${prefix} Shared assessments changed after its version override was created. Review the override and either align it with the shared methodology or keep a deliberate version-specific approach. Advisory warning (does not block editing).`,
            ...flagContext,
          });
        }

        // An unplaced module has one shared context here and an entry per version there.
        const weight = version
          ? ledgerEntries.find((entry) => entry.version?.id === version.id)
          : worstEntry(ledgerEntries);
        if (assessments.length === 0 && (weight?.incoming.length ?? 0) === 0) {
          flags.push({
            type: "error",
            msg: `${prefix} No assessments have been defined. Add at least one assessment so learners can be assessed and progression can be reviewed.`,
            ...flagContext,
          });
        } else {
          const weightingTotal = weight?.total ?? 0;
          if (!isBalanced(weightingTotal)) {
            flags.push({
              type: "error",
              msg: `${prefix} Assessment weightings total ${weightingTotal}% (should be 100%). Adjust module and participating-module contributions so the combined weighting equals 100%.`,
              ...flagContext,
            });
          }
        }

        assessments.forEach((a: ModuleAssessment) => {
          const participatingModuleIds = new Set<string>();
          (a.participatingModules ?? []).forEach((participation) => {
            const participatingModule = (p.modules ?? []).find(
              (candidate) => candidate.id === participation.moduleId,
            );
            const assessmentName = a.title || "Untitled";
            if (!participatingModule) {
              flags.push({
                type: "error",
                msg: `${prefix} Assessment '${assessmentName}' refers to a participating module that no longer exists.`,
                ...flagContext,
              });
              return;
            }
            if (
              participation.moduleId === m.id ||
              participatingModuleIds.has(participation.moduleId)
            ) {
              flags.push({
                type: "error",
                msg: `${prefix} Assessment '${assessmentName}' contains a duplicate or invalid participating module.`,
                ...flagContext,
              });
              return;
            }
            participatingModuleIds.add(participation.moduleId);

            if (
              !Number.isFinite(participation.weighting) ||
              participation.weighting <= 0 ||
              participation.weighting > 100
            ) {
              flags.push({
                type: "error",
                msg: `${prefix} Assessment '${assessmentName}': ${participatingModule.title || participatingModule.code || "participating module"} contribution must be between 1% and 100%.`,
                ...flagContext,
              });
            }

            const availableMimloIds = new Set(
              (participatingModule.mimlos ?? []).map((mimlo) => mimlo.id),
            );
            if ((participation.mimloIds ?? []).length === 0) {
              flags.push({
                type: "warn",
                msg: `${prefix} Assessment '${assessmentName}' is not mapped to any outcomes in ${participatingModule.title || participatingModule.code || "the participating module"}.`,
                ...flagContext,
              });
            } else if (participation.mimloIds.some((mimloId) => !availableMimloIds.has(mimloId))) {
              flags.push({
                type: "error",
                msg: `${prefix} Assessment '${assessmentName}' contains an invalid outcome mapping for ${participatingModule.title || participatingModule.code || "a participating module"}.`,
                ...flagContext,
              });
            }

            if (version) {
              const moduleIsInVersion = (version.stages ?? []).some((stage) =>
                (stage.modules ?? []).some(
                  (assignment) => assignment.moduleId === participation.moduleId,
                ),
              );
              if (!moduleIsInVersion) {
                flags.push({
                  type: "error",
                  msg: `${prefix} Assessment '${assessmentName}' includes ${participatingModule.title || participatingModule.code || "a module"}, but that module is not in this programme version.`,
                  ...flagContext,
                });
              }
            }
          });

          if (
            a.minimumPassMark != null &&
            (!Number.isFinite(a.minimumPassMark) ||
              a.minimumPassMark < 0 ||
              a.minimumPassMark > 100)
          ) {
            flags.push({
              type: "error",
              msg: `${prefix} Assessment '${a.title || "Untitled"}': minimum pass mark must be between 0% and 100%.`,
              ...flagContext,
            });
          } else if (a.minimumPassMark != null && a.isHurdle !== true && a.mandatoryPass !== true) {
            flags.push({
              type: "warn",
              msg: `${prefix} Assessment '${a.title || "Untitled"}': minimum pass mark is set, but the assessment is not marked as a hurdle or mandatory pass.`,
              ...flagContext,
            });
          }

          if (a.outsideTeachingPeriod === true && a.indicativeWeek == null) {
            flags.push({
              type: "warn",
              msg: `${prefix} Assessment '${a.title || "Untitled"}' in ${moduleDescriptor} occurs outside the teaching period, but its assessment week is not confirmed. Confirm the week or move it inside teaching weeks. Advisory warning (does not block editing).`,
              ...flagContext,
            });
          } else if (
            a.outsideTeachingPeriod !== true &&
            a.indicativeWeek &&
            a.indicativeWeek > teachingWeeks
          ) {
            flags.push({
              type: "warn",
              msg: `${prefix} Assessment '${a.title || "Untitled"}' in ${moduleDescriptor}: assessment week (${a.indicativeWeek}) exceeds teaching weeks (${teachingWeeks}). Mark it as outside the teaching period if this timing is intentional. Advisory warning (does not block editing).`,
              ...flagContext,
            });
          }
        });
      },
    );
  });

  const ploToMimlos = p.ploToMimlos ?? {};
  // A blank outcome's missing mapping follows from its missing text, so it is
  // listed under that finding rather than counted here.
  (p.plos ?? []).forEach((plo, index) => {
    if (isBlankOutcome(plo) && (ploToMimlos[plo.id] ?? []).length === 0) {
      flags.push({
        type: "warn",
        msg: `Programme outcome ${index + 1} is not mapped to any module outcome.`,
        step: "mapping",
        ploId: plo.id,
        nested: true,
      });
    }
  });
  const plos = (p.plos ?? []).filter((plo) => !isBlankOutcome(plo));
  const mappedPLOCount = plos.filter((o: PLO) => (ploToMimlos[o.id] ?? []).length > 0).length;
  const unmappedPLOCount = plos.length - mappedPLOCount;
  if (plos.length > 0 && unmappedPLOCount > 0) {
    if (mappedPLOCount === 0) {
      // No PLOs are mapped at all — mapping has not been started (incomplete).
      flags.push({
        type: "error",
        msg: `Some programme outcomes are not mapped to any module outcome (${unmappedPLOCount}). None are mapped yet. Map each programme outcome to the module outcomes that teach it.`,
        step: "mapping",
      });
    } else {
      // Some PLOs are mapped but not all — coverage is partial.
      flags.push({
        type: "error",
        msg: `Some programme outcomes are not mapped to any module outcome (${unmappedPLOCount}). Mapping is partially complete. Map the remaining outcomes to confirm full coverage.`,
        step: "mapping",
      });
    }
  }

  // Every module requires at least one reading-list entry before internal review.
  const modulesForReading = p.modules ?? [];
  const modulesMissingReading = modulesForReading.filter(
    (m: Module) => (m.readingList ?? []).length === 0,
  );
  if (modulesMissingReading.length > 0) {
    flags.push({
      type: "warn",
      msg: `Reading lists are incomplete: ${modulesMissingReading.length} of ${modulesForReading.length} modules have no reading-list entries. Add at least one core reference per affected module. Advisory warning (does not block editing).`,
      step: "reading-lists",
    });
  }

  return flags;
}

/**
 * Calculates the overall completion percentage of a programme.
 * Checks identity fields, structure, outcomes, and versions for completion.
 */
export function completionPercent(p: Programme): number {
  let total = 0;
  let done = 0;

  // Identity (4 items). Completion reflects required data only; award type is
  // advisory (its absence raises a warning, not an error), so it must not reduce
  // the percentage. The fourth identity slot tracks the required identity fields
  // being in place rather than the advisory award type.
  const hasTitle = Boolean(p.title && p.title.trim());
  const hasNfqLevel = Boolean(p.nfqLevel);
  const hasDiscipline = Boolean(p.discipline && p.discipline.trim());

  total += 1;
  if (hasTitle) {
    done += 1;
  }
  total += 1;
  if (hasNfqLevel) {
    done += 1;
  }
  total += 1;
  if (hasDiscipline) {
    done += 1;
  }
  total += 1;
  if (hasTitle && hasNfqLevel && hasDiscipline) {
    done += 1;
  }

  // Structure (2 items)
  total += 1;
  if ((p.totalCredits ?? 0) > 0) {
    done += 1;
  }
  total += 1;
  if ((p.modules ?? []).length > 0) {
    done += 1;
  }

  // Outcomes (2 items)
  total += 1;
  if ((p.plos ?? []).length >= outcomeBand(p).min) {
    done += 1;
  }
  total += 1;
  if (Object.keys(p.ploToMimlos ?? {}).length > 0) {
    done += 1;
  }

  // Versions (2 items)
  total += 1;
  if (Array.isArray(p.versions) && p.versions.length > 0) {
    done += 1;
  }
  const v0 = (p.versions ?? [])[0];
  total += 1;
  if (v0 && Array.isArray(v0.stages) && v0.stages.length > 0) {
    done += 1;
  }

  ["effort-hours", "assessments", "reading-lists"].forEach((stepKey) => {
    total += 1;
    if (getStepCompletion(stepKey, p) === "complete") {
      done += 1;
    }
  });

  if (isCasProgramme(p)) {
    total += 1;
    if (getStepCompletion("cas-picker", p) === "complete") {
      done += 1;
    }
  }

  return Math.round((done / total) * 100);
}
